import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Loader2, MessageSquare, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const COMMENT_MAX = 500;

/**
 * Per-slide feedback bar.
 *
 * Lives below the active-slide preview on UserDetailsPage. Each user can
 * have one feedback row per slide. Thumbs are toggleable (clicking an active
 * thumb clears the rating). Comment auto-saves on blur.
 *
 * Backend endpoints (`routes/feedback.py`):
 *   GET    /api/feedback/slide/{slide_id}
 *   POST   /api/feedback/slide/{slide_id}   { rating, comment }
 *   DELETE /api/feedback/slide/{slide_id}
 */
export default function SlideFeedbackBar({ slideId, isDeck = false }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [origComment, setOrigComment] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showComment, setShowComment] = useState(false);
  // Skip the "saved" toast on the very first auto-load
  const skipNextSavedToast = useRef(true);

  // Fetch on mount + every time slideId changes (deck navigation).
  useEffect(() => {
    if (!slideId) return;
    setLoaded(false);
    skipNextSavedToast.current = true;
    axios
      .get(`${API}/feedback/slide/${slideId}`, { withCredentials: true })
      .then((r) => {
        const fb = r.data?.my_feedback;
        setRating(fb?.rating || null);
        setComment(fb?.comment || "");
        setOrigComment(fb?.comment || "");
        setShowComment(Boolean(fb?.comment));
      })
      .catch(() => {
        // Silently fail — feedback isn't critical-path
        setRating(null);
        setComment("");
        setOrigComment("");
      })
      .finally(() => setLoaded(true));
  }, [slideId]);

  const persist = async (nextRating, nextComment) => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/feedback/slide/${slideId}`,
        { rating: nextRating, comment: (nextComment || "").trim() || null },
        { withCredentials: true },
      );
      const fb = res.data?.my_feedback;
      setRating(fb?.rating || null);
      setOrigComment(fb?.comment || "");
      if (skipNextSavedToast.current) {
        skipNextSavedToast.current = false;
      } else {
        toast.success("Feedback saved");
      }
    } catch {
      toast.error("Couldn't save feedback");
    } finally {
      setSaving(false);
    }
  };

  const handleThumb = (next) => {
    // Clicking the active thumb clears it (toggle behavior)
    const newRating = rating === next ? null : next;
    setRating(newRating);
    persist(newRating, comment);
  };

  const handleCommentBlur = () => {
    if (comment.trim() === origComment.trim()) return;
    if (comment.length > COMMENT_MAX) {
      toast.error(`Comment is too long (max ${COMMENT_MAX} characters)`);
      return;
    }
    persist(rating, comment);
  };

  if (!slideId) return null;

  const upActive = rating === "up";
  const downActive = rating === "down";

  return (
    <div
      className="flex flex-col gap-2 mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50/40"
      data-testid="slide-feedback-bar"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 mr-2">
          {isDeck ? "Was this deck useful?" : "Was this slide useful?"}
        </span>

        <button
          type="button"
          onClick={() => handleThumb("up")}
          disabled={!loaded || saving}
          aria-pressed={upActive}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
            upActive
              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          } disabled:opacity-50`}
          data-testid="feedback-thumbs-up"
          title="Thumbs up"
        >
          <ThumbsUp className={`w-4 h-4 ${upActive ? "fill-emerald-700" : ""}`} />
        </button>

        <button
          type="button"
          onClick={() => handleThumb("down")}
          disabled={!loaded || saving}
          aria-pressed={downActive}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
            downActive
              ? "bg-rose-50 border-rose-300 text-rose-700"
              : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          } disabled:opacity-50`}
          data-testid="feedback-thumbs-down"
          title="Thumbs down"
        >
          <ThumbsDown className={`w-4 h-4 ${downActive ? "fill-rose-700" : ""}`} />
        </button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowComment((s) => !s)}
          className="ml-auto text-xs text-gray-600 hover:text-gray-900"
          data-testid="feedback-toggle-comment"
        >
          <MessageSquare className="w-3.5 h-3.5 mr-1" />
          {origComment ? "Edit comment" : showComment ? "Hide" : "Add comment"}
        </Button>

        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
      </div>

      {showComment && (
        <div className="space-y-1">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
            onBlur={handleCommentBlur}
            placeholder="What worked or didn't? (optional, max 500 chars)"
            className="text-sm bg-white border-gray-200 min-h-[64px]"
            data-testid="feedback-comment-input"
          />
          <div className="flex items-center justify-between text-[11px] text-gray-400">
            <span>
              {comment.trim() === origComment.trim() && origComment ? (
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <Check className="w-3 h-3" /> Saved
                </span>
              ) : (
                "Auto-saves when you click outside"
              )}
            </span>
            <span>{comment.length}/{COMMENT_MAX}</span>
          </div>
        </div>
      )}
    </div>
  );
}
